'use strict';

const libPictProvider = require('pict-provider');
const libQRCode = require('headlight-qrcode');

const DETAIL_MOUNT = '#SQR-Detail';
const LEGACY_TOKEN = 'SiteManagerSampleGUID';
const LIMS_PRODUCT = 'LIMS';
const PRINTABLE_SUBTYPE = `${LIMS_PRODUCT}-Sample-Printable`;

// The fields that can appear on a printed sample QR label. Keys are the addresses HeadlightQuickSummaries resolves
// on a Sample record for the 'Printable' action: raw columns, computed FK names (_Material from IDMaterial,
// _CreatingUser from CreatingIDUser, …), and formatted dates (DesignatedTime_Formatted). Each carries the descriptor
// metadata the label renderer needs (HeadlightPrintable / DataType).
const FIELD_PALETTE =
[
	{ Key: 'QRCode', Name: 'Sample ID' },
	{ Key: 'LabControlNumber', Name: 'PSID' },
	{ Key: 'Name', Name: 'Description' },
	{ Key: 'ReceivedFrom', Name: 'Source' },
	{ Key: 'Status', Name: 'Status' },
	{ Key: 'Notes', Name: 'Notes' },
	{ Key: '_Material', Name: 'Material' },
	{ Key: '_Project', Name: 'Project' },
	// Not columns: QRCodeProvider.addJoinedLabelFields fills these from the Sample's SampleLineItemJoins and TestInstances.
	{ Key: '_LineItems', Name: 'Line Items' },
	{ Key: '_Tests', Name: 'Tests' },
	{ Key: '_CreatingUser', Name: 'Sampled By', HeadlightPrintable: { DisplayFields: [ 'NameFirst', 'NameLast' ] } },
	{ Key: 'DesignatedTime_Formatted', Name: 'Sampled On', DataType: 'DateTime', HeadlightPrintable: { DateFormat: 'YYYY-MM-DD' } },
];
const PALETTE_BY_KEY = FIELD_PALETTE.reduce((pMap, pField) => { pMap[pField.Key] = pField; return pMap; }, {});
// Label layouts QRCodeProvider draws; anything else is drawn as 'wide'.
const PRINT_STYLES = [ 'thin', 'wide', 'tall' ];
const normalizePrintStyle = (pStyle) => (PRINT_STYLES.indexOf(pStyle) > -1 ? pStyle : 'wide');
// Date formats offered for date fields (dayjs format strings).
const DATE_FORMATS =
[
	{ Value: 'YYYY-MM-DD', Example: '2026-10-16' },
	{ Value: 'MMM D, YYYY', Example: 'Oct 16, 2026' },
	{ Value: 'MM/DD/YYYY', Example: '10/16/2026' },
];
// Code-format profiles (headlight-qrcode Profiles). 'Unconfigured' = no QRCodes config: SiteManager when
// CustomSampleGUID says so, otherwise the customer's default (Profiles.getDefaultProfileName).
const PROFILE_CHOICES =
[
	{ Value: 'Standard', Title: 'Standard', Description: 'Project + user + date + random characters. Never repeats; the format new customers get.' },
	{ Value: 'Legacy-Classic', Title: 'Pre-2026 web format', Description: 'Project + user + timestamp, exactly as samples were coded before 2026. For customers with systems that read these codes.' },
	{ Value: 'Legacy-SiteManager', Title: 'SiteManager / Legacy', Description: 'Sampler’s SiteManager id + timestamp. Use when samples must be found in SiteManager.' },
];
const DEFAULT_CONTENT_FIELDS =
[
	{ Key: 'QRCode', Name: 'Sample ID', Row: 1, Column: 1 },
	{ Key: '_Material', Name: 'Material', Row: 1, Column: 2 },
	{ Key: '_Project', Name: 'Project', Row: 2, Column: 1 },
	{ Key: 'DesignatedTime_Formatted', Name: 'Sampled On', Row: 2, Column: 2 },
];

/**
 * SampleQRCodeConfig — the bespoke #/SampleQRCodeConfig screen. Two tabs:
 *   • Format — how the QR string is generated: the customer's QRCodes/Profile configuration (Customer level),
 *     with CustomProperties.CustomSampleGUID kept in step for older readers.
 *   • Label content — what prints ON the QR label (title + fields laid out in rows/columns), stored as the
 *     customer's `LIMS-Sample-Printable` ManifestSchema (Type=RecordManifest) — the same manifest the LIMS app +
 *     the config app's Print Label read via QRCodeProvider.ensurePrintableManifests().
 * Gated by the `SampleQRCode-Configure` entitlement. Super-user may pick any customer; a customer-admin edits own.
 */
class SampleQRCodeConfigProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get qrService() { return (this.pict.fable.services && this.pict.fable.services.HeadlightQRCodeHash) || this.pict.fable.HeadlightQRCodeHash; }
	get qrProvider() { return this.pict.providers.QRCodeProvider; }

	get state()
	{
		if (!this.pict.AppData.SampleQRCodeConfig)
		{
			this.pict.AppData.SampleQRCodeConfig =
			{
				Loaded: false, Loading: false, Saving: false, Error: '', Message: '',
				Tab: 'format', Format: 'Standard', TargetCustomerID: 0, TargetCustomerName: '',
				Customers: [], PreviewCode: '', PreviewPayload: '',
				// Label-content tab
				ContentTitle: 'Sample', PrintStyle: 'wide', ContentFields: [], ContentSaving: false,
				ContentMessage: '', ContentError: '', PreviewSampleQR: '', _printableIDManifestSchema: 0,
				// Printing tab
				DefaultMediaPreset: '', PrintingConfigured: false, PrintingSaving: false, PrintingMessage: '', PrintingError: '',
			};
		}
		return this.pict.AppData.SampleQRCodeConfig;
	}

	get currentUser() { return this.pict.AppData.CurrentUser || {}; }
	callerCustomer() { return Number(this.currentUser.CustomerID) || 0; }
	isSuperUser() { return (Number(this.currentUser.UserRoleIndex) === 5) && (Number(this.currentUser.CustomerID) === 1); }

	can()
	{
		const tmpSvc = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpSvc || (typeof tmpSvc.can !== 'function')) { return true; }
		return tmpSvc.can('SampleQRCode', 'Configure');
	}

	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_putJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.putJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_postJSON(pURL, pBody) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.postJSON(pURL, pBody, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	_parseProps(pRecord)
	{
		let tmpProps = (pRecord && pRecord.CustomProperties) || {};
		if (typeof tmpProps === 'string')
		{
			const tmpTrimmed = tmpProps.trim();
			if (tmpTrimmed.startsWith('{')) { try { tmpProps = JSON.parse(tmpTrimmed); } catch (pError) { tmpProps = {}; } }
			else { tmpProps = {}; }
		}
		return (tmpProps && typeof tmpProps === 'object') ? tmpProps : {};
	}

	/** The profile the editor currently describes, resolved the same way apps resolve it. */
	_editedProfile()
	{
		const tmpState = this.state;
		if (tmpState.Profile === 'Unconfigured')
		{
			return this._unconfiguredProfile();
		}
		return libQRCode.Profiles.resolveProfile(this._profileConfig(), {});
	}

	/** What the target customer gets with no QRCodes config. */
	_unconfiguredProfile()
	{
		const tmpState = this.state;
		return libQRCode.Profiles.resolveProfile({}, (tmpState.Format === 'SiteManager') ? { CustomSampleGUID: LEGACY_TOKEN } : {}, { IDCustomer: tmpState.TargetCustomerID });
	}

	/**
	 * @param {string} pProfileName
	 * @return {string} the plain-language title shown for a profile
	 */
	_profileTitle(pProfileName)
	{
		const tmpChoice = PROFILE_CHOICES.find((pChoice) => pChoice.Value === pProfileName);
		return tmpChoice ? tmpChoice.Title : pProfileName;
	}

	/** The QRCodes/Profile configuration object for the edited profile. */
	_profileConfig()
	{
		const tmpState = this.state;
		const tmpConfig = { Profile: tmpState.Profile, AllowCompact: !!tmpState.AllowCompact };
		// The sample-ID-only choice is offered for the legacy formats only, so it is saved only with them.
		if (tmpState.Envelope && (tmpState.Profile === 'Legacy-Classic' || tmpState.Profile === 'Legacy-SiteManager')) { tmpConfig.Envelope = tmpState.Envelope; }
		// Without explicit options every minter uses the materials app's shape (9 characters, 12-hour), so only write them when edited.
		if (tmpState.Profile === 'Legacy-SiteManager' && tmpState.SiteManagerOptionsSet)
		{
			tmpConfig.SiteManager = { MaxPrefixLength: Number(tmpState.SiteManagerMaxPrefix) || 9, HourFormat: (tmpState.SiteManagerHourFormat === '24') ? '24' : '12' };
		}
		return tmpConfig;
	}

	_computePreviews()
	{
		const tmpState = this.state;
		try
		{
			const tmpProfile = this._editedProfile();
			const tmpMinter = new libQRCode.QRCodeMinter();
			const tmpPrefix = this.currentUser.ExternalSyncGUID || `HL${Number(this.currentUser.UserID) || 0}`;
			const tmpContext = (tmpProfile.CodeFormat === 'SiteManager') ? { ExternalSyncGUID: tmpPrefix } : { IDProject: 0, IDUser: Number(this.currentUser.UserID) || 0 };
			const tmpCode = tmpMinter.mint(tmpProfile.CodeFormat, tmpContext, tmpProfile.SiteManager);
			const tmpCodec = new libQRCode.QRCodeCodec();
			tmpState.PreviewCode = tmpCode;
			tmpState.PreviewPayload = tmpCodec.encode({ Entity: 'Sample', Value: tmpCode }, { Envelope: tmpProfile.Envelope, Environment: libQRCode.Hosts.getEnvironmentFromHostname((typeof window !== 'undefined' && window.location) ? window.location.hostname : '') });
		}
		catch (pError)
		{
			tmpState.PreviewCode = '(preview unavailable)';
			tmpState.PreviewPayload = '';
		}
	}

	setTab(pTab)
	{
		this.state.Tab = ([ 'content', 'printing' ].includes(pTab)) ? pTab : 'format';
		this.render();
		if (this.state.Tab === 'content')
		{
			this._renderPreviewLater();
		}
	}

	async loadAsync()
	{
		const tmpState = this.state;
		tmpState.Loading = true; tmpState.Error = ''; tmpState.Message = '';
		this.render();
		try
		{
			if (this.isSuperUser())
			{
				const tmpCustomers = await this._getJSON('Customers/FilteredTo/FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/2000');
				tmpState.Customers = Array.isArray(tmpCustomers) ? tmpCustomers.map((pC) => ({ IDCustomer: pC.IDCustomer, Name: pC.Name })) : [];
			}
			else { tmpState.Customers = []; }
			if (!tmpState.TargetCustomerID) { tmpState.TargetCustomerID = this.callerCustomer(); }
			await this._loadCustomerAsync(tmpState.TargetCustomerID);
			this._computePreviews();
			await this._loadContentAsync(tmpState.TargetCustomerID);
			tmpState.Loaded = true;
		}
		catch (pError)
		{
			tmpState.Error = `Could not load the QR-code configuration: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	async _loadCustomerAsync(pIDCustomer)
	{
		const tmpState = this.state;
		const tmpRecord = pIDCustomer ? await this._getJSON(`Customer/${pIDCustomer}`) : null;
		tmpState.TargetCustomerName = (tmpRecord && tmpRecord.Name) ? tmpRecord.Name : '';
		const tmpProps = this._parseProps(tmpRecord);
		tmpState.Format = (tmpProps.CustomSampleGUID === LEGACY_TOKEN) ? 'SiteManager' : 'Standard';
		const tmpMap = pIDCustomer ? await this._getJSON(`Configuration/For/QRCodes/In/Customer/${pIDCustomer}`) : null;
		const tmpConfig = (tmpMap && tmpMap.Profile && typeof tmpMap.Profile === 'object') ? tmpMap.Profile : {};
		tmpState.ProfileConfigured = !!tmpConfig.Profile;
		tmpState.Profile = tmpConfig.Profile || 'Unconfigured';
		tmpState.AllowCompact = tmpConfig.AllowCompact === true;
		tmpState.Envelope = tmpConfig.Envelope || '';
		tmpState.SiteManagerOptionsSet = !!tmpConfig.SiteManager;
		tmpState.SiteManagerMaxPrefix = (tmpConfig.SiteManager && Number(tmpConfig.SiteManager.MaxPrefixLength)) || 9;
		tmpState.SiteManagerHourFormat = (tmpConfig.SiteManager && tmpConfig.SiteManager.HourFormat === '24') ? '24' : '12';
		const tmpPrinting = (tmpMap && tmpMap.Printing && typeof tmpMap.Printing === 'object') ? tmpMap.Printing : {};
		tmpState.PrintingConfigured = !!tmpPrinting.DefaultMediaPreset;
		tmpState.DefaultMediaPreset = tmpPrinting.DefaultMediaPreset || '';
		tmpState.PrintingMessage = '';
		tmpState.PrintingError = '';
		this._computePreviews();
	}

	// --- Label content: load the LIMS-Sample-Printable ManifestSchema for the target customer -----------------
	async _loadContentAsync(pIDCustomer)
	{
		const tmpState = this.state;
		tmpState._printableIDManifestSchema = 0;
		let tmpManifest = null;
		try
		{
			const tmpRows = await this._getJSON(`ManifestSchemas/FilteredTo/FBV~Type~EQ~RecordManifest~FBV~SubType~EQ~${PRINTABLE_SUBTYPE}/0/50`);
			const tmpRow = Array.isArray(tmpRows) ? tmpRows.find((pRow) => Number(pRow.IDCustomer) === Number(pIDCustomer)) : null;
			if (tmpRow)
			{
				tmpState._printableIDManifestSchema = tmpRow.IDManifestSchema;
				tmpManifest = (tmpRow.Manifest && typeof tmpRow.Manifest === 'object') ? tmpRow.Manifest
					: (typeof tmpRow.Manifest === 'string' ? JSON.parse(tmpRow.Manifest) : null);
			}
		}
		catch (pError) { /* fall through to defaults */ }

		if (tmpManifest && tmpManifest.Descriptors && Object.keys(tmpManifest.Descriptors).length)
		{
			tmpState.ContentTitle = tmpManifest.TitleTemplate || 'Sample';
			tmpState.PrintStyle = normalizePrintStyle(tmpManifest.PrintStyle);
			const tmpFields = Object.keys(tmpManifest.Descriptors).map((pKey) =>
			{
				const tmpDesc = tmpManifest.Descriptors[pKey] || {};
				const tmpField = { Key: pKey, Name: (typeof tmpDesc.Name === 'string') ? tmpDesc.Name : pKey, Row: Number(tmpDesc.Row) || 1, Column: Number(tmpDesc.Column) || 1 };
				if (tmpDesc.HeadlightPrintable && tmpDesc.HeadlightPrintable.DateFormat) { tmpField.DateFormat = tmpDesc.HeadlightPrintable.DateFormat; }
				return tmpField;
			});
			tmpFields.sort((pA, pB) => (pA.Row - pB.Row) || (pA.Column - pB.Column));
			tmpState.ContentFields = tmpFields;
		}
		else
		{
			tmpState.ContentTitle = 'Sample';
			tmpState.PrintStyle = 'wide';
			tmpState.ContentFields = DEFAULT_CONTENT_FIELDS.map((pField) => Object.assign({}, pField));
		}

		// A recent sample QR for the live preview (scoped to the caller's customer; best-effort).
		try
		{
			const tmpSamples = await this._getJSON('Samples/FilteredTo/FBV~Deleted~EQ~0~FSF~CreateDate~DESC~0/0/1');
			tmpState.PreviewSampleQR = (Array.isArray(tmpSamples) && tmpSamples.length && tmpSamples[0].QRCode) ? tmpSamples[0].QRCode : '';
		}
		catch (pError) { tmpState.PreviewSampleQR = ''; }
	}

	async selectCustomer(pIDCustomer)
	{
		const tmpState = this.state;
		tmpState.TargetCustomerID = Number(pIDCustomer) || 0;
		tmpState.Message = ''; tmpState.Error = ''; tmpState.ContentMessage = ''; tmpState.ContentError = '';
		if (tmpState.TargetCustomerID)
		{
			await this._loadCustomerAsync(tmpState.TargetCustomerID);
			await this._loadContentAsync(tmpState.TargetCustomerID);
		}
		this.render();
		if (tmpState.Tab === 'content') { this._renderPreviewLater(); }
	}

	// --- Format tab ------------------------------------------------------------------------------------------
	setProfile(pProfile)
	{
		const tmpKnown = PROFILE_CHOICES.some((pChoice) => pChoice.Value === pProfile);
		this.state.Profile = tmpKnown ? pProfile : 'Unconfigured';
		this.state.Message = '';
		this._computePreviews();
		this.render();
	}

	setProfileOption(pOption, pValue)
	{
		const tmpState = this.state;
		if (pOption === 'AllowCompact')
		{
			tmpState.AllowCompact = !!pValue;
		}
		else if (pOption === 'SampleIDOnly')
		{
			tmpState.Envelope = pValue ? 'Raw' : '';
		}
		else if (pOption === 'MaxPrefix')
		{
			tmpState.SiteManagerOptionsSet = true;
			tmpState.SiteManagerMaxPrefix = Math.max(1, Math.min(20, Number(pValue) || 9));
		}
		else if (pOption === 'HourFormat')
		{
			tmpState.SiteManagerOptionsSet = true;
			tmpState.SiteManagerHourFormat = (pValue === '24') ? '24' : '12';
		}
		tmpState.Message = '';
		this._computePreviews();
		this.render();
	}

	async save()
	{
		const tmpState = this.state;
		if (tmpState.Saving)
		{
			return;
		}
		if (!tmpState.TargetCustomerID)
		{
			tmpState.Error = 'Pick a customer first.';
			this.render();
			return;
		}
		if (tmpState.Profile === 'Unconfigured')
		{
			tmpState.Error = 'Choose a format to save.';
			this.render();
			return;
		}
		tmpState.Saving = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			const tmpConfigResult = await this._postJSON(`Configuration/Put/QRCodes/Profile/Customer/${Number(tmpState.TargetCustomerID)}`, { Config: this._profileConfig() });
			if (tmpConfigResult && tmpConfigResult.error)
			{
				throw new Error(tmpConfigResult.error.message || String(tmpConfigResult.error));
			}
			// Older readers (the iPad materials app, sampleplan) still look at CustomSampleGUID; keep it in step.
			const tmpRecord = await this._getJSON(`Customer/${tmpState.TargetCustomerID}`);
			const tmpProps = this._parseProps(tmpRecord);
			const tmpWantsSiteManager = (tmpState.Profile === 'Legacy-SiteManager');
			if (tmpWantsSiteManager !== (tmpProps.CustomSampleGUID === LEGACY_TOKEN))
			{
				if (tmpWantsSiteManager)
				{
					tmpProps.CustomSampleGUID = LEGACY_TOKEN;
				}
				else
				{
					delete tmpProps.CustomSampleGUID;
				}
				const tmpResult = await this._putJSON('Customer', { IDCustomer: Number(tmpState.TargetCustomerID), CustomProperties: JSON.stringify(tmpProps) });
				if (tmpResult && tmpResult.error)
				{
					throw new Error(tmpResult.error.message || String(tmpResult.error));
				}
			}
			tmpState.Format = tmpWantsSiteManager ? 'SiteManager' : 'Standard';
			tmpState.ProfileConfigured = true;
			tmpState.Message = `Saved. New samples for ${tmpState.TargetCustomerName || 'this customer'} will use “${this._profileTitle(tmpState.Profile)}”. People already signed in pick it up when they next sign in.`;
		}
		catch (pError)
		{
			tmpState.Error = `Save failed: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Saving = false;
			this.render();
		}
	}

	// --- Label content tab: editor ops -----------------------------------------------------------------------
	setContentTitle(pValue) { this.state.ContentTitle = pValue; }
	setPrintStyle(pStyle) { this.state.PrintStyle = normalizePrintStyle(pStyle); this.state.ContentMessage = ''; this.render(); this._renderPreviewLater(); }
	setFieldName(pIdx, pValue) { const tmpF = this.state.ContentFields[pIdx]; if (tmpF) { tmpF.Name = pValue; this._renderPreviewLater(); } }
	setFieldDateFormat(pIdx, pValue) { const tmpF = this.state.ContentFields[pIdx]; if (tmpF) { tmpF.DateFormat = pValue; this._renderPreviewLater(); } }
	setFieldRow(pIdx, pValue) { const tmpF = this.state.ContentFields[pIdx]; if (tmpF) { tmpF.Row = Math.max(1, Number(pValue) || 1); this._renderPreviewLater(); } }
	setFieldColumn(pIdx, pValue) { const tmpF = this.state.ContentFields[pIdx]; if (tmpF) { tmpF.Column = Math.max(1, Number(pValue) || 1); this._renderPreviewLater(); } }
	removeField(pIdx) { this.state.ContentFields.splice(pIdx, 1); this.state.ContentMessage = ''; this.render(); this._renderPreviewLater(); }
	addField(pKey)
	{
		const tmpPalette = PALETTE_BY_KEY[pKey];
		if (!tmpPalette) { return; }
		if (this.state.ContentFields.some((pF) => pF.Key === pKey)) { return; }
		const tmpMaxRow = this.state.ContentFields.reduce((pMax, pF) => Math.max(pMax, Number(pF.Row) || 1), 0);
		this.state.ContentFields.push({ Key: pKey, Name: tmpPalette.Name, Row: tmpMaxRow + 1, Column: 1 });
		this.state.ContentMessage = '';
		this.render();
		this._renderPreviewLater();
	}

	/** Build the printable manifest (typeConfig['Sample'] shape) from the editor state. */
	_buildManifest()
	{
		const tmpState = this.state;
		const tmpDescriptors = {};
		for (const tmpField of tmpState.ContentFields)
		{
			const tmpPalette = PALETTE_BY_KEY[tmpField.Key] || {};
			const tmpDescriptor = { Hash: tmpField.Key, Name: (typeof tmpField.Name === 'string') ? tmpField.Name.trim() : tmpField.Key, Row: Number(tmpField.Row) || 1, Column: Number(tmpField.Column) || 1 };
			if (tmpPalette.DataType) { tmpDescriptor.DataType = tmpPalette.DataType; }
			if (tmpPalette.HeadlightPrintable) { tmpDescriptor.HeadlightPrintable = Object.assign({}, tmpPalette.HeadlightPrintable); }
			if (tmpPalette.DataType === 'DateTime' && tmpField.DateFormat) { tmpDescriptor.HeadlightPrintable = Object.assign(tmpDescriptor.HeadlightPrintable || {}, { DateFormat: tmpField.DateFormat }); }
			tmpDescriptors[tmpField.Key] = tmpDescriptor;
		}
		return { Scope: 'Sample-Printable', TitleTemplate: tmpState.ContentTitle || 'Sample', PrintStyle: normalizePrintStyle(tmpState.PrintStyle), Descriptors: tmpDescriptors };
	}

	async saveContent()
	{
		const tmpState = this.state;
		if (tmpState.ContentSaving) { return; }
		if (!tmpState.TargetCustomerID) { tmpState.ContentError = 'Pick a customer first.'; this.render(); return; }
		if (!tmpState.ContentFields.length) { tmpState.ContentError = 'Add at least one field to the label.'; this.render(); return; }
		tmpState.ContentSaving = true; tmpState.ContentError = ''; tmpState.ContentMessage = '';
		this.render();
		try
		{
			const tmpManifest = this._buildManifest();
			let tmpResult;
			if (tmpState._printableIDManifestSchema)
			{
				tmpResult = await this._putJSON('ManifestSchema', { IDManifestSchema: Number(tmpState._printableIDManifestSchema), Manifest: tmpManifest });
			}
			else
			{
				tmpResult = await this._postJSON('ManifestSchema',
				{
					Hash: PRINTABLE_SUBTYPE, Type: 'RecordManifest', SubType: PRINTABLE_SUBTYPE, Title: PRINTABLE_SUBTYPE,
					Version: 1, IDCustomer: Number(tmpState.TargetCustomerID), Manifest: tmpManifest,
				});
				const tmpNew = tmpResult && tmpResult.result;
				if (tmpNew && tmpNew.IDManifestSchema) { tmpState._printableIDManifestSchema = tmpNew.IDManifestSchema; }
			}
			if (tmpResult && tmpResult.error) { throw new Error(tmpResult.error.message || String(tmpResult.error)); }
			// Apply to the live QRCodeProvider so Print Label reflects it immediately.
			if (this.qrProvider && typeof this.qrProvider.addManifestForType === 'function') { this.qrProvider.addManifestForType('Sample', tmpManifest); }
			tmpState.ContentMessage = 'Saved. Printed sample labels will show this content.';
		}
		catch (pError) { tmpState.ContentError = `Save failed: ${(pError && pError.message) || pError}`; }
		finally { tmpState.ContentSaving = false; this.render(); this._renderPreviewLater(); }
	}

	/** Render a live label preview into #SQR-Preview using a real sample + the current (unsaved) manifest. */
	_renderPreviewLater() { setTimeout(() => { this.renderPreview(); }, 30); }
	async renderPreview()
	{
		const tmpState = this.state;
		const tmpContainer = (typeof document !== 'undefined') ? document.querySelector('#SQR-Preview') : null;
		if (!tmpContainer) { return; }
		if (!tmpState.PreviewSampleQR) { tmpContainer.innerHTML = '<div class="sqr-preview-empty">No sample available to preview a label.</div>'; return; }
		const tmpQR = this.qrProvider;
		if (!tmpQR || typeof tmpQR.printDecoratedEntity !== 'function') { tmpContainer.innerHTML = '<div class="sqr-preview-empty">Label preview is not available.</div>'; return; }
		const tmpTypeConfig = tmpQR.typeConfig || {};
		const tmpSavedManifest = tmpTypeConfig.Sample;
		const tmpSavedEnsured = tmpQR._PrintableManifestsEnsured;
		const tmpPreviewManifest = this._buildManifest();
		try
		{
			// Apply the CURRENT (possibly unsaved) manifest so the preview reflects the edits in progress.
			if (typeof tmpQR.addManifestForType === 'function')
			{
				tmpQR.addManifestForType('Sample', tmpPreviewManifest);
			}
			tmpQR._PrintableManifestsEnsured = true;   // don't let a lazy load clobber the applied manifest
			await tmpQR.printDecoratedEntity('Sample', tmpState.PreviewSampleQR, 'SQR-Preview');
		}
		catch (pError) { tmpContainer.innerHTML = `<div class="sqr-preview-empty">Could not render the preview: ${this._esc((pError && pError.message) || pError)}</div>`; }
		finally
		{
			// Labels printed elsewhere in the app keep the saved layout until Save (which applies its own).
			if (tmpQR.typeConfig && (tmpQR.typeConfig.Sample === tmpPreviewManifest))
			{
				if (tmpSavedManifest)
				{
					tmpQR.typeConfig.Sample = tmpSavedManifest;
				}
				else
				{
					delete tmpQR.typeConfig.Sample;
				}
			}
			tmpQR._PrintableManifestsEnsured = tmpSavedEnsured;
		}
	}

	// --- render ----------------------------------------------------------------------------------------------
	render()
	{
		if (!this.pict.views.SampleQRCodeConfig) { return; }
		const tmpState = this.state;
		if (!this.can())
		{
			this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, `<div class="sqr-empty">You don’t have the <strong>Configure sample QR codes</strong> entitlement.</div>`);
			return;
		}
		if (tmpState.Loading && !tmpState.Loaded) { this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, '<div class="sqr-empty"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>'); return; }

		const tmpCustomerPicker = this.isSuperUser()
			? `<div class="sqr-field"><label class="sqr-field-label">Customer</label>
				<select class="sqr-select" onchange="_Pict.providers.SampleQRCodeConfig.selectCustomer(this.value)">
					${(tmpState.Customers || []).map((pC) => `<option value="${pC.IDCustomer}" ${Number(pC.IDCustomer) === Number(tmpState.TargetCustomerID) ? 'selected' : ''}>${this._esc(pC.Name)} (#${pC.IDCustomer})</option>`).join('')}
				</select></div>`
			: `<div class="sqr-field"><label class="sqr-field-label">Customer</label><div class="sqr-customer">${this._esc(tmpState.TargetCustomerName || ('#' + tmpState.TargetCustomerID))}</div></div>`;

		const tmpTabs = `<div class="sqr-tabs">
			<button class="sqr-tab${tmpState.Tab === 'format' ? ' is-active' : ''}" onclick="_Pict.providers.SampleQRCodeConfig.setTab('format')">QR format</button>
			<button class="sqr-tab${tmpState.Tab === 'content' ? ' is-active' : ''}" onclick="_Pict.providers.SampleQRCodeConfig.setTab('content')">Label content</button>
			<button class="sqr-tab${tmpState.Tab === 'printing' ? ' is-active' : ''}" onclick="_Pict.providers.SampleQRCodeConfig.setTab('printing')">Printing</button>
		</div>`;

		let tmpBody = this._formatHTML();
		if (tmpState.Tab === 'content')
		{
			tmpBody = this._contentHTML();
		}
		else if (tmpState.Tab === 'printing')
		{
			tmpBody = this._printingHTML();
		}
		this.pict.ContentAssignment.assignContent(DETAIL_MOUNT, `${tmpCustomerPicker}${tmpTabs}${tmpBody}`);
	}

	_formatHTML()
	{
		const tmpState = this.state;
		const tmpMsg = tmpState.Message ? `<div class="sqr-msg">${this._esc(tmpState.Message)}</div>` : '';
		const tmpErr = tmpState.Error ? `<div class="sqr-error">${this._esc(tmpState.Error)}</div>` : '';
		const tmpCurrent = tmpState.ProfileConfigured ? '' : `<div class="sqr-note"><i class="fas fa-circle-info"></i> No format has been chosen for this customer yet, so new samples use “${this._esc(this._profileTitle(this._unconfiguredProfile().Name))}”. Choosing one here makes it explicit.</div>`;
		const tmpOptions = PROFILE_CHOICES.map((pChoice) => this._optionHTML(pChoice.Value, tmpState.Profile === pChoice.Value, pChoice.Title, pChoice.Description)).join('');
		const tmpSiteManager = (tmpState.Profile === 'Legacy-SiteManager')
			? `<div class="sqr-field"><label class="sqr-field-label">SiteManager id length</label>
					<input class="sqr-input sqr-rcnum" type="number" min="1" max="20" value="${Number(tmpState.SiteManagerMaxPrefix) || 9}" onchange="_Pict.providers.SampleQRCodeConfig.setProfileOption('MaxPrefix', this.value)"> characters at most</div>
				<div class="sqr-field"><label class="sqr-field-label">Clock</label>
					<select class="sqr-select" onchange="_Pict.providers.SampleQRCodeConfig.setProfileOption('HourFormat', this.value)">
						<option value="12" ${tmpState.SiteManagerHourFormat !== '24' ? 'selected' : ''}>12-hour (materials app)</option>
						<option value="24" ${tmpState.SiteManagerHourFormat === '24' ? 'selected' : ''}>24-hour</option>
					</select></div>`
			: '';
		const tmpSampleIDOnly = (tmpState.Profile === 'Legacy-Classic' || tmpState.Profile === 'Legacy-SiteManager')
			? `<div class="sqr-field"><label class="sqr-option-desc"><input type="checkbox" ${tmpState.Envelope === 'Raw' ? 'checked' : ''} onclick="_Pict.providers.SampleQRCodeConfig.setProfileOption('SampleIDOnly', this.checked)"> Put only the sample ID in the code, as the Materials app does. A phone camera then shows the ID instead of opening the sample.</label></div>`
			: '';
		const tmpCompact = (tmpState.Profile === 'Standard')
			? `<div class="sqr-field"><label class="sqr-option-desc"><input type="checkbox" ${tmpState.AllowCompact ? 'checked' : ''} onclick="_Pict.providers.SampleQRCodeConfig.setProfileOption('AllowCompact', this.checked)"> Print short labels (fit small stickers). Turn on only once every scanner this customer uses has been updated.</label></div>`
			: '';
		return `${tmpErr}${tmpMsg}${tmpCurrent}
			<div class="sqr-field-label">QR code format</div>
			<div class="sqr-options">${tmpOptions}</div>
			${tmpSiteManager}${tmpSampleIDOnly}${tmpCompact}
			<div class="sqr-option-preview"><span class="sqr-preview-label">Example code</span> <code>${this._esc(tmpState.PreviewCode || '')}</code></div>
			<div class="sqr-option-preview"><span class="sqr-preview-label">Printed as</span> <code>${this._esc(tmpState.PreviewPayload || '')}</code></div>
			<div class="sqr-note"><i class="fas fa-circle-info"></i> The example is generated by the same code new samples use. The SiteManager id comes from each sampler’s SiteManager id (<code>ExternalSyncGUID</code>), falling back to <code>HL&lt;user id&gt;</code>.</div>
			<div class="sqr-savebar"><button class="sqr-save" ${tmpState.Saving ? 'disabled' : ''} onclick="_Pict.providers.SampleQRCodeConfig.save()">${tmpState.Saving ? 'Saving…' : 'Save QR format'}</button></div>`;
	}

	// --- Printing tab: the customer's default label stock (QRCodes/Printing configuration) ------------------
	/** @return {Array<{ Id: string, Name: string }>} the label stocks the print dialog offers */
	_mediaPresets()
	{
		const tmpHL = this.pict.providers.HeadlightQR;
		return (tmpHL && typeof tmpHL.getMediaPresets === 'function') ? tmpHL.getMediaPresets() : [];
	}

	setDefaultMediaPreset(pPresetID)
	{
		this.state.DefaultMediaPreset = this._mediaPresets().some((pPreset) => pPreset.Id === pPresetID) ? pPresetID : '';
		this.state.PrintingMessage = '';
		this.state.PrintingError = '';
		this.render();
	}

	async savePrinting()
	{
		const tmpState = this.state;
		if (tmpState.PrintingSaving)
		{
			return;
		}
		if (!tmpState.TargetCustomerID || !tmpState.DefaultMediaPreset)
		{
			tmpState.PrintingError = !tmpState.TargetCustomerID ? 'Pick a customer first.' : 'Choose a label stock to save.';
			this.render();
			return;
		}
		tmpState.PrintingSaving = true;
		tmpState.PrintingError = '';
		tmpState.PrintingMessage = '';
		this.render();
		try
		{
			const tmpResult = await this._postJSON(`Configuration/Put/QRCodes/Printing/Customer/${Number(tmpState.TargetCustomerID)}`, { Config: { DefaultMediaPreset: tmpState.DefaultMediaPreset } });
			if (tmpResult && tmpResult.error)
			{
				throw new Error(tmpResult.error.message || String(tmpResult.error));
			}
			tmpState.PrintingConfigured = true;
			if ((Number(tmpState.TargetCustomerID) === this.callerCustomer()) && this.pict.providers.HeadlightQR)
			{
				this.pict.providers.HeadlightQR.setCustomerPrintSettings({ DefaultMediaPreset: tmpState.DefaultMediaPreset });
			}
			tmpState.PrintingMessage = `Saved. Print uses this label stock for ${tmpState.TargetCustomerName || 'this customer'}, except on devices where someone picked a different stock in Print options. Apps already open pick it up when they are reloaded.`;
		}
		catch (pError)
		{
			tmpState.PrintingError = `Save failed: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.PrintingSaving = false;
			this.render();
		}
	}

	_printingHTML()
	{
		const tmpState = this.state;
		const tmpPresets = this._mediaPresets();
		const tmpHL = this.pict.providers.HeadlightQR;
		const tmpAppDefaultName = ((tmpHL && tmpPresets.find((pPreset) => pPreset.Id === tmpHL.options.DefaultMediaPreset)) || {}).Name || '';
		const tmpMsg = tmpState.PrintingMessage ? `<div class="sqr-msg">${this._esc(tmpState.PrintingMessage)}</div>` : '';
		const tmpErr = tmpState.PrintingError ? `<div class="sqr-error">${this._esc(tmpState.PrintingError)}</div>` : '';
		const tmpCurrent = tmpState.PrintingConfigured ? '' : `<div class="sqr-note"><i class="fas fa-circle-info"></i> No label stock has been chosen for this customer yet, so Print uses “${this._esc(tmpAppDefaultName || 'the first stock in the list')}”.</div>`;
		const tmpOptions = [ `<option value="" ${tmpState.DefaultMediaPreset ? '' : 'selected'} disabled>Choose a label stock</option>` ]
			.concat(tmpPresets.map((pPreset) => `<option value="${this._esc(pPreset.Id)}" ${pPreset.Id === tmpState.DefaultMediaPreset ? 'selected' : ''}>${this._esc(pPreset.Name)}</option>`)).join('');
		return `${tmpErr}${tmpMsg}${tmpCurrent}
			<div class="sqr-field"><label class="sqr-field-label">Default label stock</label>
				<select class="sqr-select" onchange="_Pict.providers.SampleQRCodeConfig.setDefaultMediaPreset(this.value)">${tmpOptions}</select></div>
			<div class="sqr-note"><i class="fas fa-circle-info"></i> Print sends labels straight to the printer on this stock. Print options lets someone pick another stock and see the label first; their choice is remembered on that device only.</div>
			<div class="sqr-savebar"><button class="sqr-save" ${tmpState.PrintingSaving ? 'disabled' : ''} onclick="_Pict.providers.SampleQRCodeConfig.savePrinting()">${tmpState.PrintingSaving ? 'Saving…' : 'Save label stock'}</button></div>`;
	}

	_optionHTML(pValue, pActive, pTitle, pDesc)
	{
		return `<label class="sqr-option${pActive ? ' is-active' : ''}">
			<input type="radio" name="sqr-format" ${pActive ? 'checked' : ''} onclick="_Pict.providers.SampleQRCodeConfig.setProfile('${pValue}')">
			<div class="sqr-option-body">
				<div class="sqr-option-title">${this._esc(pTitle)}</div>
				<div class="sqr-option-desc">${this._esc(pDesc)}</div>
			</div>
		</label>`;
	}

	_contentHTML()
	{
		const tmpState = this.state;
		const tmpMsg = tmpState.ContentMessage ? `<div class="sqr-msg">${this._esc(tmpState.ContentMessage)}</div>` : '';
		const tmpErr = tmpState.ContentError ? `<div class="sqr-error">${this._esc(tmpState.ContentError)}</div>` : '';
		const tmpUsedKeys = {};
		const tmpRows = tmpState.ContentFields.map((pField, pIdx) =>
		{
			tmpUsedKeys[pField.Key] = true;
			const tmpPalette = PALETTE_BY_KEY[pField.Key];
			const tmpDateFormat = pField.DateFormat || (tmpPalette && tmpPalette.HeadlightPrintable && tmpPalette.HeadlightPrintable.DateFormat) || DATE_FORMATS[0].Value;
			const tmpDateHTML = (tmpPalette && tmpPalette.DataType === 'DateTime')
				? `<select class="sqr-input sqr-datefmt" title="Date format" onchange="_Pict.providers.SampleQRCodeConfig.setFieldDateFormat(${pIdx}, this.value)">${DATE_FORMATS.map((pFormat) => `<option value="${this._esc(pFormat.Value)}" ${pFormat.Value === tmpDateFormat ? 'selected' : ''}>${this._esc(pFormat.Example)}</option>`).join('')}</select>`
				: '';
			return `<div class="sqr-fieldrow">
				<span class="sqr-fieldkey">${this._esc(PALETTE_BY_KEY[pField.Key] ? PALETTE_BY_KEY[pField.Key].Key : pField.Key)}</span>
				<input class="sqr-input sqr-fieldname" value="${this._esc(pField.Name)}" oninput="_Pict.providers.SampleQRCodeConfig.setFieldName(${pIdx}, this.value)" placeholder="No label (value only)">
				${tmpDateHTML}
				<label class="sqr-rc">Row <input class="sqr-input sqr-rcnum" type="number" min="1" value="${Number(pField.Row) || 1}" onchange="_Pict.providers.SampleQRCodeConfig.setFieldRow(${pIdx}, this.value)"></label>
				${tmpState.PrintStyle === 'wide' ? `<label class="sqr-rc">Col <input class="sqr-input sqr-rcnum" type="number" min="1" max="2" value="${Number(pField.Column) || 1}" onchange="_Pict.providers.SampleQRCodeConfig.setFieldColumn(${pIdx}, this.value)"></label>` : ''}
				<button class="sqr-fieldremove" title="Remove" onclick="_Pict.providers.SampleQRCodeConfig.removeField(${pIdx})"><i class="fas fa-times"></i></button>
			</div>`;
		}).join('');
		const tmpAvail = FIELD_PALETTE.filter((pField) => !tmpUsedKeys[pField.Key]);
		const tmpAvailHTML = tmpAvail.length
			? tmpAvail.map((pField) => `<button class="sqr-avail" onclick="_Pict.providers.SampleQRCodeConfig.addField('${pField.Key}')" title="${this._esc(pField.Key)}"><i class="fas fa-plus"></i> ${this._esc(pField.Name)}</button>`).join('')
			: '<span class="sqr-avail-empty">All available fields are on the label.</span>';

		return `${tmpErr}${tmpMsg}
			<div class="sqr-field"><label class="sqr-field-label">Label title</label>
				<input class="sqr-input" style="max-width:20rem" value="${this._esc(tmpState.ContentTitle)}" oninput="_Pict.providers.SampleQRCodeConfig.setContentTitle(this.value)" placeholder="Sample"></div>
			<div class="sqr-field"><label class="sqr-field-label">Layout</label>
				<div class="sqr-styletoggle">
					<button class="sqr-stylebtn${tmpState.PrintStyle === 'thin' ? ' is-active' : ''}" onclick="_Pict.providers.SampleQRCodeConfig.setPrintStyle('thin')">Thin (1 column)</button>
					<button class="sqr-stylebtn${tmpState.PrintStyle === 'wide' ? ' is-active' : ''}" onclick="_Pict.providers.SampleQRCodeConfig.setPrintStyle('wide')">Wide (2 columns)</button>
					<button class="sqr-stylebtn${tmpState.PrintStyle === 'tall' ? ' is-active' : ''}" onclick="_Pict.providers.SampleQRCodeConfig.setPrintStyle('tall')">Large code (1 column)</button>
				</div>
				${tmpState.PrintStyle === 'tall' ? '<div class="sqr-note"><i class="fas fa-circle-info"></i> The code takes most of the label’s width, with the lines below it in one size. A long value wraps onto up to three lines.</div>' : ''}</div>
			<div class="sqr-field-label">Fields on the label</div>
			<div class="sqr-fields">${tmpRows || '<div class="sqr-avail-empty">No fields yet — add some below.</div>'}</div>
			<div class="sqr-field-label" style="margin-top:0.8rem">Add a field</div>
			<div class="sqr-availwrap">${tmpAvailHTML}</div>
			<div class="sqr-preview-wrap"><div class="sqr-field-label">Label preview</div><div id="SQR-Preview" class="sqr-preview"></div></div>
			<div class="sqr-savebar"><button class="sqr-save" ${tmpState.ContentSaving ? 'disabled' : ''} onclick="_Pict.providers.SampleQRCodeConfig.saveContent()">${tmpState.ContentSaving ? 'Saving…' : 'Save label content'}</button></div>`;
	}
}

module.exports = SampleQRCodeConfigProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'SampleQRCodeConfig',
	AutoInitialize: true,
	AutoInitializeOrdinal: 231,
};
